import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  fromCanonicalKg,
  isNetworkError,
  nextUnit,
  toCanonicalKg,
  type UnitsPreference,
  parseLocaleNumber,
} from "@heraion/shared";
import { convertWeightText } from "../convertWeightText";
import type { TFn } from "../i18nTypes";
import { writeErrorCode, type ConfirmFn, type DurableWriteResult } from "../seams";

/** An alias, no longer a second declaration of the same enum. The name stays
 *  because the ui barrel exports it and both PostSessionSummaryCard mounts
 *  import it from there; the rename is a follow-up, not this fix. What the
 *  alias buys today is that the two names can no longer drift apart. */
export type WeightUnit = UnitsPreference;

/** A MAX write, as the athlete meant it — set a value or clear it.
 *  `source` is always "tested" here: this editor is the athlete typing a
 *  number they achieved, never a derived estimate (the post-workout pill is
 *  the one that writes "derived"). */
export type MaxWrite =
  | {
      kind: "set";
      exerciseId: string;
      valueKg: number;
      source: "tested";
    }
  | { kind: "clear"; exerciseId: string };

/**
 * ⛔ Host seam for MAX writes specifically, NOT the generic `DurableWriteFn`,
 * and the distinction is a data-correctness one rather than a style choice.
 *
 * `/api/me/maxes/:slug` already has a dedicated mobile queue (`maxOutbox`),
 * because the post-workout "new MAX?" confirm writes there too. Routing this
 * editor onto the generic request queue instead would put TWO queues on the
 * same resource, and they drain concurrently with no ordering between them —
 * so an athlete who confirms a bump offline and then corrects it by hand
 * would get whichever request happened to resolve last, not their last
 * action. On a 1RM. `maxOutbox`'s own header anticipated this wiring and
 * asked for exactly this: reuse that queue.
 *
 * It lives here and not in `seams.ts` because that module is concept-neutral
 * by contract — it knows what a request is, never what a MAX is.
 */
export type MaxWriteFn = (op: MaxWrite) => Promise<DurableWriteResult>;

export type UseMaxEditorDeps = {
  writeMax: MaxWriteFn;
  /** The current { exerciseId → kg } map (to seed the input + gate Clear). */
  maxes: Map<string, number>;
  /**
   * ⭐ THE MX-6 story, told once. MaxesView.tsx's `units` prop points here;
   * the two screen mounts carry no comment on purpose — there the required
   * prop typechecks the mistake away.
   *
   * The athlete's own unit. It seeds both the chip and the value on every open,
   * and the chip stays a per-row override that dies with the row, because a
   * leg-press plated in pounds can sit next to a rack in kilos and you must be
   * able to type what the machine says.
   *
   * ⛔ This was a hardcoded "kg" from the day the screen was born (v1.15.0,
   * 2026-05-09) and rode the lift into this package verbatim. An athlete on
   * pounds opened a MAX, read "KG", typed 225 meaning pounds, and stored 225 kg
   * — 2.2× wrong, silently, on the number every %MAX prescription is computed
   * from. Nothing failed, because a hardcoded default is a valid value of the
   * type; the profile preference had existed since four days before the screen.
   */
  units: UnitsPreference;
  /** Re-fetch after a mutation so consumers see the new value immediately. */
  forceRefresh: () => Promise<unknown>;
  confirm: ConfirmFn;
};

// Edit orchestration for the Maxes surface — open/edit state + the
// PUT/DELETE mutations against /api/me/maxes/:id. Lifted from mobile's
// MaxesScreen so both mounts share one mutation path; the view stays
// presentational. Errors surface as either a localized invalid message or
// a raw `api_error_<status>` code (preserved from the mobile original).
// #397: a rejection no server answered (offline, a timeout: status 0) says the
// network, never "Enter a valid weight": the parse already passed. Anything
// else with no status (not an ApiError) is a plain failed save.
const rejectionText = (t: TFn, result: DurableWriteResult): string => {
  const code = writeErrorCode(result);
  if (code !== null) return code;
  const error = result.outcome === "rejected" ? result.error : null;
  const noAnswer =
    isNetworkError(error) || (error as { status?: unknown } | null)?.status === 0;
  return noAnswer
    ? t("inbox.actionErrorOffline", {
        defaultValue: "This needs a connection. Reconnect and try again.",
      })
    : t("mobile.maxes.saveFailed", { defaultValue: "Couldn't save. Try again." });
};

export function useMaxEditor(deps: UseMaxEditorDeps) {
  const { t } = useTranslation();
  const { maxes, forceRefresh, confirm, units } = deps;

  const [editingSlug, setEditingSlug] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  // The stored kg the field was seeded from, kept so a chip toggle can convert
  // from the exact value instead of from its rounded display — two roundings
  // in a row turn 100 kg into 100.023 kg, on a number the athlete never touched.
  const [editSeedKg, setEditSeedKg] = useState<number | null>(null);
  const [editUnit, setEditUnit] = useState<UnitsPreference>(units);
  const [editBusy, setEditBusy] = useState<"save" | "clear" | null>(null);
  const [editError, setEditError] = useState<string | null>(null);

  const openEdit = useCallback(
    (slug: string) => {
      const current = maxes.get(slug);
      setEditingSlug(slug);
      setEditSeedKg(current ?? null);
      setEditUnit(units);
      setEditValue(
        current !== undefined ? String(fromCanonicalKg(current, units)) : "",
      );
      setEditError(null);
    },
    [maxes, units],
  );

  const closeEdit = useCallback(() => {
    setEditBusy((busy) => {
      if (busy === null) setEditingSlug(null);
      return busy;
    });
  }, []);

  /**
   * ⛔ The chip CONVERTS the number, it does not relabel it (Antenore, 26/08).
   * The field opens holding a value the server sent, so a chip that only
   * swapped its caption would leave 220.5 on screen now meaning 220.5 kg where
   * a moment ago it meant 100 kg — the same corruption the `units` doc above
   * describes, reached by a tap instead of by a default. The physical weight
   * never moves on its own; typing still overwrites it.
   */
  const toggleUnit = useCallback(() => {
    const from = editUnit;
    // CN-40 — `nextUnit`, not the ternary it was built to delete. It was born
    // one slice ago with a single caller, and this line sat next to the file
    // that slice was editing.
    const to = nextUnit(from);
    // ⭐ Il «riparti dal kg seminato invece che dalla sua visualizzazione
    // arrotondata» ora sta DENTRO `convertWeightText` (CN-26): qui c'era la
    // scorciatoia che si dichiarava tale, in attesa del primitivo condiviso.
    setEditValue((text) => convertWeightText(text, from, to, editSeedKg));
    setEditUnit(to);
  }, [editUnit, editSeedKg]);

  const save = useCallback(async () => {
    if (!editingSlug || editBusy !== null) return;
    // ⛔ parseLocaleNumber, non parseFloat: il campo e' `decimal-pad`, quindi
    // su tastiera it/fr/de il separatore decimale e' la VIRGOLA e
    // `parseFloat("100,5")` si ferma alla virgola e restituisce 100. Mezzo chilo
    // in meno su un massimale, in silenzio. Reso coerente col resto del form,
    // che dal 26/08 converte l'unita' passando dallo stesso parser.
    const parsed = parseLocaleNumber(editValue);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setEditError(t("mobile.maxes.errInvalid"));
      return;
    }
    setEditBusy("save");
    setEditError(null);
    try {
      const valueKg = toCanonicalKg(parsed, editUnit);
      const result = await deps.writeMax({
        kind: "set",
        exerciseId: editingSlug,
        valueKg,
        source: "tested",
      });
      if (result.outcome === "rejected") {
        setEditError(rejectionText(t, result));
        return;
      }
      // sent or queued: the write is durable either way — work by default,
      // no distinct UI (offline-writes.md §IL SEAM DI SCRITTURA). A queued
      // write's forceRefresh legitimately fails offline; that is not an
      // error to surface, so it is swallowed here rather than caught below.
      await forceRefresh().catch(() => undefined);
      setEditingSlug(null);
    } finally {
      setEditBusy(null);
    }
  }, [editingSlug, editBusy, editValue, editUnit, deps, forceRefresh, t]);

  const clear = useCallback(async () => {
    if (!editingSlug || editBusy !== null) return;
    const ok = await confirm({
      title: t("mobile.maxes.clearTitle"),
      body: t("mobile.maxes.clearBody"),
      confirmLabel: t("mobile.maxes.clearConfirm"),
      cancelLabel: t("mobile.maxes.clearCancel"),
    });
    if (!ok) return;
    setEditBusy("clear");
    setEditError(null);
    try {
      const result = await deps.writeMax({
        kind: "clear",
        exerciseId: editingSlug,
      });
      if (result.outcome === "rejected") {
        setEditError(rejectionText(t, result));
        return;
      }
      await forceRefresh().catch(() => undefined);
      setEditingSlug(null);
    } finally {
      setEditBusy(null);
    }
  }, [editingSlug, editBusy, confirm, deps, forceRefresh, t]);

  return {
    editingSlug,
    editValue,
    editUnit,
    editBusy,
    editError,
    canClear: maxes.has(editingSlug ?? ""),
    setEditValue,
    openEdit,
    closeEdit,
    toggleUnit,
    save,
    clear,
  };
}
